import styled, { css } from 'styled-components';

import { colors } from 'chime-ui/system';

import svgs from 'theme/svgs';

/* Shared Mixins */
const center = css`
  ${props => props.center && 'text-align: center;'}
`;

export const ghostButtonMixin = css`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

/* Shared Styled Components */
export const Label = styled.label`
  display: inline-block;
  font-size: ${props => props.theme.styles.label.fontSize};
  color: ${props => props.theme.styles.label.color};
  font-weight: ${props => props.theme.styles.label.fontWeight};
  line-height: ${props => props.theme.styles.label.lineHeight};
  margin: ${props => props.theme.styles.label.margin};
  position: ${props => props.theme.styles.label.position};
  transition: ${props => props.theme.styles.label.transition};

  ${props =>
    props.isSelect &&
    css`
      transition: ${props.theme.styles.label.selectInput.transition};
    `};

  ${props =>
    props.isEmpty &&
    props.theme.styles.label.empty &&
    css`
      top: ${props => props.theme.styles.label.empty.top};
      left: ${props => props.theme.styles.label.empty.left};
      font-size: ${props => props.theme.styles.label.empty.fontSize};
      cursor: text;
    `}

  ${props =>
    (!props.isEmpty || props.isFocused || props.isReadOnly) &&
    props.theme.styles.label.focusedOrFilled &&
    css`
      top: ${props => props.theme.styles.label.focusedOrFilled.top};
      left: ${props => props.theme.styles.label.focusedOrFilled.left};
      font-size: ${props => props.theme.styles.label.focusedOrFilled.fontSize};
      color: ${colors['content-primary']};
    `};
`;

export const DescriptionText = styled.p`
  ${center}
  padding-left: 2px;
  margin: 1rem 0;
  color: ${colors['content-primary']};
  font-size: ${props => props.theme.styles.descriptionText.fontSize};
  line-height: ${props => props.theme.styles.descriptionText.lineHeight};
  font-weight: 300;
`;

export const SlimDescriptionText = styled(DescriptionText)`
  margin: 8px 0 24px;
`;

export const ListItemText = styled(DescriptionText)`
  margin: 16px 0 16px;
`;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${props =>
    props.showFooterDisclosure ? 'start' : 'space-between'};
  margin: 0 auto;
  padding: 0 23px 50px;

  ${props => css`
    ${props.theme.media.tablet`
        padding-top: 65px;
        padding-bottom: 50px;
        max-width: 588px;
      `};

    ${props.theme.media.desktop`
        padding-left: 0;
        padding-right: 0;
      `};
  `};
`;

// Originally called StepContainer
export const Card = styled.div`
  background: ${props => props.theme.colors.white};
  padding-bottom: ${props => (props.showFooterDisclosure ? '0' : '24px')};

  ${props => css`
    ${props.theme.media.tablet`
        height: auto;
        margin-bottom: 80px;
      `};
    ${props.theme.media.desktop`
        padding-top: 26px;
        border-radius: 24px;
        box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.08), 0 24px 32px 0 rgba(0, 0, 0, 0.04);
        margin-bottom: 112px;
      `};
    ${props => props.theme.media.mobileOnly`
        min-height: ${props =>
          props.showFooterDisclosure ? 'max-content' : '100vh'};
      `};
  `};
`;

export const CardContainer = styled.div`
  ${props => css`
    ${props.theme.media.desktop`
        padding: 0px 80px 35px;
      `};
  `};
`;

const PageTitleMixin = css`
  ${center}
  /* We use a lower top margin here to compensate for extra padding so when page auto scrolls
  to title there is sufficient spacing between top of viewport and title */
  margin: -6px 0 10px;
  font-weight: ${props => props.theme.styles.titles.fontWeight};
  font-family: ${props => props.theme.fonts.headerFont};
  padding-top: 14px;
  padding-bottom: ${props => props.theme.styles.titles.paddingBottom};
  text-overflow: ellipsis;
  overflow: hidden;
  letter-spacing: -0.2px;
  color: ${colors['content-primary']};

  ${props => css`
    ${props.theme.media.tablet`
        margin: 2px 0 10px;
        padding-top: 0;
      `};
    ${props.theme.media.desktop`
        margin: 10px 0 10px;
      `};
  `};
`;

export const PageTitle = styled.h1`
  ${PageTitleMixin}
  font-size: ${props => props.theme.styles.titles.fontSize};
  line-height: ${props => props.theme.styles.titles.lineHeight};
`;

export const P2PPageTitle = styled.h1`
  ${PageTitleMixin}
  font-size: 21px;
  line-height: 28px;
`;

const LogoStyling = styled.img`
  display: block;
  margin: 25px auto 12px;
  width: 86px;
  height: auto;

  ${props => css`
    ${props.theme.media.tablet`
        margin: 21px auto 14px;
      `};
    ${props.theme.media.desktop`
        margin: 12px auto 22px;
      `};
  `};
`;

export const FinePrint = styled.small`
  margin-bottom: 38px;
  align-self: center;
`;

/** Shared React Styled Stateless Components */

export const ChimeLogo = styled(LogoStyling).attrs({
  src: svgs.headerLogo,
  alt: 'Chime logo',
})``;

export const ChimeLogoOnly = styled.img.attrs({
  src: svgs.headerLogo,
  alt: 'Chime logo',
})`
  width: 86px;
  height: auto;
`;

export const PostEnrollmentChimeLogo = styled(ChimeLogo)`
  margin: 0;
  grid-column: 1 / span 2;
  justify-self: center;
  display: ${props => (props.alwaysShow ? 'block' : 'none')};

  ${props => css`
    ${props.theme.media.tablet`
        display: block;
      `};
  `};
`;

const currentYear = new Date().getFullYear();

export const Copyright = () => (
  <FinePrint>&copy; {`${currentYear} Chime. All Rights Reserved.`}</FinePrint>
);

export const Registered = () => <sup>®</sup>;

/** Shared CSS */
export const commonInputStyles = css`
  width: 100%;
  font-weight: 400;
  font-family: inherit;
  color: inherit;
  outline: none;
  appearance: none;
  background-color: transparent;

  position: ${props => props.theme.styles.inputs.position};
  font-size: ${props => props.theme.styles.inputs.fontSize};
  height: ${props => props.theme.styles.inputs.height};
  line-height: 22px;
  padding: ${props => props.theme.styles.inputs.padding};
  border: ${props =>
    `solid ${props.theme.styles.inputs.borderWidth} ${colors['border-primary']}`};
  border-radius: ${props => props.theme.styles.inputs.borderRadius};

  &:focus {
    outline: 6px solid ${colors['bg-tertiary']};
    border: ${props => props.theme.styles.inputs.borderWidth} solid
      ${colors['border-action-primary']};
    &::placeholder {
      color: ${colors['content-secondary']};
      opacity: 1;
    }
  }

  ${props =>
    props.hasError &&
    css`
      background-color: ${colors['utility-alert100']};
      border-color: ${colors['utility-alert500']};
      outline: 6px solid ${colors['utility-alert200']};
      &:focus {
        background-color: ${colors['utility-alert100']};
        border-color: ${colors['utility-alert500']};
        outline: 6px solid ${colors['utility-alert200']};
      }
    `};

  &::placeholder {
    opacity: 0;
  }

  &:-ms-input-placeholder {
    opacity: 1;
    color: transparent;
  }

  &::-ms-clear {
    display: none;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-gap: 1rem;
`;

export const SuccessDescription = styled.p`
  color: ${colors['content-primary']};
  font-family: ${props => props.theme.fonts.primaryFont};
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  margin: 0;

  ${props => css`
    ${props.theme.media.tablet`
        text-align: left;
      `};
  `};
`;

export const VerificationSecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  text-decoration: none;
  margin: 32px auto 0;
  color: ${props => props.theme.colors.darkGreen};
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  border: 0 none;
  background: none;
`;

export const VerificationResendCodeButton = styled.button`
  ${ghostButtonMixin}
  ont-weight: 500;
  margin: 0.5rem 0;
  text-decoration: underline;
  color: ${props => props.theme.colors['content-primary']};
  cursor: pointer;
`;

export const ChecklistDisclaimerText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 12px;
  line-height: 17px;
  color: ${colors['content-primary']};

  ${props => css`
    ${props.theme.media.mobileOnly`
        font-weight: 500;
      `};
  `};
`;
